import React, { useState, useEffect } from 'react';
import { useStudy } from '../context/StudyContext';
import './CategorySelector.css';

function CategorySelector({ studySetId, onSelect, onBack }) {
  const { getCategories } = useStudy();
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryProgress, setCategoryProgress] = useState({});

  // Get category progress from localStorage
  const getCategoryProgress = (studySetId) => {
    const key = `progress_${studySetId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  };

  // Get mode completions for a category
  const getCategoryModeCompletions = (studySetId, categoryName) => {
    const key = `mode_completion_${studySetId}_${categoryName}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  };

  useEffect(() => {
    loadCategories();
    // Load progress data
    const progress = getCategoryProgress(studySetId);
    setCategoryProgress(progress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studySetId]);

  const loadCategories = () => {
    const data = getCategories(studySetId);
    setCategories(data.categories);
    setLoading(false);
  };

  // Get progress icon for a category
  const getProgressIcon = (categoryName) => {
    const progress = categoryProgress[categoryName];
    if (!progress || progress.attempted === 0) {
      return { icon: '○', status: 'unattempted' };
    }
    const percentage = (progress.correct / progress.attempted) * 100;
    if (percentage === 100) {
      return { icon: '✓', status: 'perfect' };
    }
    return { icon: '⊖', status: 'partial' };
  };

  const toggleCategory = (categoryName) => {
    if (selectedCategories.includes(categoryName)) {
      setSelectedCategories(selectedCategories.filter(c => c !== categoryName));
    } else {
      setSelectedCategories([...selectedCategories, categoryName]);
    }
  };

  const selectAll = () => {
    setSelectedCategories(categories.map(c => c.name));
  };

  const clearAll = () => {
    setSelectedCategories([]);
  };

  const clearProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress for this study set? This cannot be undone.')) {
      const progressKey = `progress_${studySetId}`;
      localStorage.removeItem(progressKey);
      
      // Clear mode completions for all categories
      categories.forEach(category => {
        const modeKey = `mode_completion_${studySetId}_${category.name}`;
        localStorage.removeItem(modeKey);
      });
      
      setCategoryProgress({});
      // Force re-render by reloading categories
      loadCategories();
    }
  };

  const handleContinue = () => {
    // Pass empty array (not null) to indicate "all questions" when no categories selected
    // This allows the app to progress to mode selection
    onSelect(selectedCategories.length > 0 ? selectedCategories : []);
  };

  if (loading) {
    return (
      <div className="category-selector">
        <div className="loading">Loading categories...</div>
      </div>
    );
  }

  const totalSelectedQuestions = selectedCategories.length > 0
    ? categories
        .filter(c => selectedCategories.includes(c.name))
        .reduce((sum, c) => sum + c.count, 0)
    : categories.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="category-selector">
      <button className="back-button" onClick={onBack}>
        ← Back
      </button>

      <div className="selector-header">
        <h2>Choose Topics to Study</h2>
        <p className="subtitle">
          Select specific categories or study all topics
        </p>
      </div>

      {categories.length === 0 ? (
        <div className="no-categories">
          <p>This study set doesn't have categorized questions.</p>
          <button className="continue-button" onClick={handleContinue}>
            Continue with All Questions
          </button>
        </div>
      ) : (
        <>
          <div className="selection-controls">
            <button className="control-button" onClick={selectAll}>
              ✓ Select All
            </button>
            <button className="control-button" onClick={clearAll}>
              ✗ Clear All
            </button>
            <button className="control-button reset-button" onClick={clearProgress}>
              🔄 Clear Progress
            </button>
            <div className="question-count">
              {totalSelectedQuestions} question{totalSelectedQuestions !== 1 ? 's' : ''} selected
            </div>
          </div>

          <div className="categories-grid">
            {categories.map((category) => (
              <div
                key={category.name}
                className={`category-card ${selectedCategories.includes(category.name) ? 'selected' : ''}`}
                onClick={() => toggleCategory(category.name)}
              >
                <div className={`category-icon progress-${getProgressIcon(category.name).status}`}>
                  {getProgressIcon(category.name).icon}
                </div>
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <span className="category-count">
                    {category.count} question{category.count !== 1 ? 's' : ''}
                  </span>
                  {(() => {
                    const completions = getCategoryModeCompletions(studySetId, category.name);
                    const hasCompletions = Object.keys(completions).some(mode => completions[mode]);
                    return hasCompletions && (
                      <div className="category-mode-completions">
                        {completions['beginner'] && <span className="mode-badge-mini beginner" title="Beginner: 100%">🌱</span>}
                        {completions['multiple-choice'] && <span className="mode-badge-mini multiple-choice" title="Multiple Choice: 100%">📊</span>}
                        {completions['free-response'] && <span className="mode-badge-mini free-response" title="Free Response: 100%">✍️</span>}
                      </div>
                    );
                  })()}
                </div>
                {selectedCategories.includes(category.name) && (
                  <div className="selected-indicator">✓</div>
                )}
              </div>
            ))}
          </div>

          <div className="selector-footer">
            <button
              className="continue-button"
              onClick={handleContinue}
              disabled={loading}
            >
              Continue to Mode Selection
              {selectedCategories.length > 0 && ` (${totalSelectedQuestions} questions)`}
            </button>
            {selectedCategories.length === 0 && (
              <p className="hint-text">
                No categories selected - all questions will be included
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CategorySelector;
